import { useMemo } from 'react';
import type { ConversationOverview, Message } from '@/types/messages.ts';
import { isDebugMessage } from '@/utils/isDebugMessage.ts';
import { conversationSwitches, type ConversationGroup } from './conversationGroups.ts';
import type { PlacedDirectorDecisions } from './placeDirectorDecisions.ts';
import { placeFindings, type PlacedFinding } from './placeFindings.ts';
import { useDirectorDecisions } from './useDirectorDecisions.ts';
import { useTranscript, type Transcript } from './useTranscript.ts';

export interface TranscriptContentInput {
  selection: ConversationGroup | null;
  /** Every conversation of the person, whichever is being read; undefined while loading. */
  conversations: ConversationOverview[] | undefined;
  showInternal: boolean;
  groupByChannel: boolean;
}

/* One empty list for every render, so the memos below keep what they computed. */
const NO_CONVERSATIONS: ConversationOverview[] = [];

export interface TranscriptContent {
  transcript: Transcript;
  /** The messages drawn: the internal ones only when asked for. */
  shown: Message[];
  director: PlacedDirectorDecisions;
  /** The messages that start another conversation, by index. */
  switches: Map<number, ConversationOverview>;
  findings: PlacedFinding[];
  notesByMessage: Map<string, PlacedFinding[]>;
}

/** The transcript being read, and everything placed in it: dividers, findings, director decisions. */
export function useTranscriptContent({
  selection,
  conversations: loadedConversations,
  showInternal,
  groupByChannel,
}: TranscriptContentInput): TranscriptContent {
  const conversations = loadedConversations ?? NO_CONVERSATIONS;
  const transcript = useTranscript(selection);
  const { messages, loading, hasMore, merged } = transcript;

  const shown = useMemo(
    () =>
      showInternal ? messages : messages.filter((message) => !isDebugMessage(message)),
    [messages, showInternal],
  );

  const director = useDirectorDecisions(shown, conversations);

  const switches = useMemo(
    () => conversationSwitches(shown, conversations, groupByChannel && merged),
    [shown, conversations, groupByChannel, merged],
  );

  const findings = useMemo(
    () =>
      placeFindings({
        conversations: selection?.conversations ?? [],
        loaded: messages,
        shown,
        complete: !loading && !hasMore,
      }),
    [selection, messages, shown, loading, hasMore],
  );

  const notesByMessage = useMemo(() => byMessage(findings), [findings]);

  return { transcript, shown, director, switches, findings, notesByMessage };
}

/** The findings that sit under a message, by that message. */
function byMessage(findings: PlacedFinding[]): Map<string, PlacedFinding[]> {
  const notes = new Map<string, PlacedFinding[]>();
  for (const placed of findings) {
    if (placed.messageId === null) continue;
    notes.set(placed.messageId, [...(notes.get(placed.messageId) ?? []), placed]);
  }
  return notes;
}
